import { QuantitySelectorComponent } from '@theme/component-quantity-selector';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   * Minus button is never disabled - when quantity is 1, it removes the item
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const effectiveMax = this.getEffectiveMax();

    // Minus button is never disabled - when qty is 1, it removes item
    minusButton.disabled = false;
    plusButton.disabled = effectiveMax !== null && this.getCurrentValues().value >= effectiveMax;
  }

  /**
   * Override decrease to remove item when quantity would go to 0
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    const { quantityInput } = this.refs;
    const { min, step, value } = this.getCurrentValues();

    // If current value is at minimum (1), set to 0 to remove the item
    if (value <= min) {
      quantityInput.value = '0';
      this.dispatchEvent(new QuantitySelectorUpdateEvent(0, Number(quantityInput.dataset.cartLine) || undefined));
      return;
    }

    // Otherwise decrease normally
    this.updateQuantity(-1);
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
